import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHeader from '../../../../components/SectionHeader';
import { useGetEmpresaQuery } from '../../../../services/companyApi';
import { useGetUsersQuery } from '../../../../services/usersApi';
import {
    useCreateTechnicalVisitMutation,
    useDownloadTechnicalVisitPdfMutation,
    useGetVisitaByIdQuery,
    useGetTechnicalVisitQuery,
    useGetTechnicalVisitQuestionsQuery,
    usePatchTechnicalVisitMutation
} from '../../../../services/visitasApi';
import { CommonMimeType, downloadBase64File } from '../../../../utils/base64FileDownload';
import { getApiErrorMessage } from '../../../../utils/apiError';
import EstablishmentDrawer from './components/EstablishmentDrawer';
import TechnicalVisitForm from './components/TechnicalVisitForm';
import TechnicalVisitHeaderData from './components/TechnicalVisitHeaderData';
import { useTechnicalVisitData } from './hooks/useTechnicalVisitData';
import {
    TechnicalVisitPayloadResponse,
    TechnicalVisitPreventorUser
} from './types/technicalVisit.types';
import {
    getTechnicalVisitResponses,
    normalizeTechnicalVisitFormDefinition,
    resolveTechnicalVisitId
} from './utils/technicalVisitUtils';

const TechnicalVisit = () => {
    const { t } = useTranslation('visitas');
    const location = useLocation();
    const navigate = useNavigate();
    const selectedVisita = location.state?.selectedVisita;
    // 'view' es solo lectura: si la visita admite edicion, la ejecucion ya abre en 'edit' (SMP-370).
    const viewMode = location.state?.mode === 'view';
    const [isEstablishmentDrawerOpen, setIsEstablishmentDrawerOpen] = useState(false);
    const [technicalVisitId, setTechnicalVisitId] = useState<number | null>(
        resolveTechnicalVisitId(selectedVisita)
    );

    const { data: currentVisitaResponse } = useGetVisitaByIdQuery(
        { idVisita: Number(selectedVisita?.id) },
        { skip: !selectedVisita?.id }
    );

    const currentVisita = currentVisitaResponse?.data || selectedVisita;

    const selectedEstablishmentId =
        location.state?.selectedEstablishmentId ||
        currentVisita?.idEstablecimiento ||
        currentVisita?.establecimiento?.id;

    const { data: establishmentData, isFetching: isFetchingEstablishment } = useGetEmpresaQuery(
        selectedEstablishmentId,
        {
            skip: !selectedEstablishmentId
        }
    );

    const establecimiento = establishmentData || currentVisita?.establecimiento || {};
    const preventorUserId = currentVisita?.preventor?.idUsuario;

    const { data: preventorUserResponse, isFetching: isFetchingPreventorUser } = useGetUsersQuery(
        {
            id: preventorUserId,
            skip: 0,
            take: 1
        },
        { skip: !preventorUserId }
    );

    const preventorUser = preventorUserResponse?.data?.data?.[0] as
        | TechnicalVisitPreventorUser
        | undefined;

    const {
        data: technicalVisitQuestionsResponse,
        isFetching: isFetchingQuestions,
        isError: isQuestionsError,
        error: technicalVisitQuestionsError
    } = useGetTechnicalVisitQuestionsQuery(
        { idVisita: Number(selectedVisita?.id) },
        { skip: !selectedVisita?.id }
    );

    const {
        data: technicalVisitDetailResponse,
        isFetching: isFetchingTechnicalVisit,
        isError: isTechnicalVisitError,
        error: technicalVisitDetailError
    } = useGetTechnicalVisitQuery(
        {
            idVisita: Number(selectedVisita?.id),
            idVisitaTecnica: technicalVisitId as number
        },
        {
            skip: !selectedVisita?.id || !technicalVisitId,
            refetchOnMountOrArgChange: true
        }
    );

    const [createTechnicalVisit, { isLoading: isCreatingTechnicalVisit }] =
        useCreateTechnicalVisitMutation();
    const [patchTechnicalVisit, { isLoading: isPatchingTechnicalVisit }] =
        usePatchTechnicalVisitMutation();
    const [downloadTechnicalVisitPdf, { isLoading: isDownloadingTechnicalVisitPdf }] =
        useDownloadTechnicalVisitPdfMutation();

    useEffect(() => {
        const nextTechnicalVisitId =
            resolveTechnicalVisitId(technicalVisitDetailResponse) ||
            resolveTechnicalVisitId(currentVisita) ||
            resolveTechnicalVisitId(selectedVisita);

        if (nextTechnicalVisitId) {
            setTechnicalVisitId(nextTechnicalVisitId);
        }
    }, [selectedVisita, currentVisita, technicalVisitDetailResponse]);

    const formDefinition = useMemo(
        () => normalizeTechnicalVisitFormDefinition(technicalVisitQuestionsResponse),
        [technicalVisitQuestionsResponse]
    );

    const initialResponses = useMemo(
        () => getTechnicalVisitResponses(technicalVisitDetailResponse),
        [technicalVisitDetailResponse]
    );

    const { establishmentName, contractFields, establishmentFields, responsibleFields } =
        useTechnicalVisitData({
            selectedVisita: currentVisita,
            establecimiento,
            preventorUser,
            t
        });

    const isSubmitting = isCreatingTechnicalVisit || isPatchingTechnicalVisit;
    const readOnly = viewMode;

    const handleSubmit = async (respuestas: TechnicalVisitPayloadResponse[]) => {
        if (!selectedVisita?.id) return;

        try {
            if (technicalVisitId) {
                const response = await patchTechnicalVisit({
                    idVisita: selectedVisita.id,
                    idVisitaTecnica: technicalVisitId,
                    respuestas
                }).unwrap();

                const nextTechnicalVisitId = resolveTechnicalVisitId(response);
                if (nextTechnicalVisitId) {
                    setTechnicalVisitId(nextTechnicalVisitId);
                }
            } else {
                const response = await createTechnicalVisit({
                    idVisita: selectedVisita.id,
                    respuestas
                }).unwrap();

                const nextTechnicalVisitId = resolveTechnicalVisitId(response);
                if (nextTechnicalVisitId) {
                    setTechnicalVisitId(nextTechnicalVisitId);
                }
            }

            enqueueSnackbar(t('technicalVisit.form.saveSuccess'), {
                variant: 'success'
            });
            navigate(-1);
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('technicalVisit.form.saveError'), {
                variant: 'error'
            });
        }
    };

    const handleDownloadPdf = async () => {
        if (!selectedVisita?.id) return;

        try {
            const response = (await downloadTechnicalVisitPdf({
                idVisita: selectedVisita.id
            }).unwrap()) as Blob | { data?: string } | string;

            if (response instanceof Blob) {
                const url = window.URL.createObjectURL(response);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `visita-tecnica_${selectedVisita.id}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return;
            }

            const base64Data =
                typeof response === 'string'
                    ? response
                    : typeof (response as { data?: string })?.data === 'string'
                      ? response.data
                      : '';

            if (!base64Data) {
                throw new Error('Invalid PDF response');
            }

            downloadBase64File({
                base64Data,
                fileName: `visita-tecnica_${selectedVisita.id}.pdf`,
                mimeType: CommonMimeType.PDF
            });
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('technicalVisit.form.downloadError'), {
                variant: 'error'
            });
        }
    };

    if (!selectedVisita?.id) {
        return (
            <Box sx={{ mt: 3 }}>
                <Alert severity="warning">{t('technicalVisit.form.visitRequired')}</Alert>
                <Box sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={() => navigate(-1)}>
                        {t('common.goBack')}
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <SectionHeader>
                <SectionHeader.Title>
                    {t('technicalVisit.title', { establishmentName })}
                </SectionHeader.Title>

                <SectionHeader.IconHeader
                    showIcon={false}
                    variant="outlined"
                    text={t('filters.establishment')}
                    onClick={() => setIsEstablishmentDrawerOpen(true)}
                    disabled={!selectedEstablishmentId}
                />
            </SectionHeader>

            <TechnicalVisitHeaderData
                contractFields={contractFields}
                establishmentFields={establishmentFields}
                responsibleFields={responsibleFields}
                isFetching={isFetchingEstablishment || isFetchingPreventorUser}
                titles={{
                    contractData: t('technicalVisit.sections.contractData'),
                    establishmentData: t('technicalVisit.sections.establishmentData'),
                    responsibleData: t('technicalVisit.sections.responsibleData')
                }}
            />

            {isFetchingQuestions || (technicalVisitId && isFetchingTechnicalVisit) ? (
                <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
                    <CircularProgress size={28} />
                </Stack>
            ) : null}

            {isQuestionsError && (
                <Alert severity="error" sx={{ mt: 3 }}>
                    {getApiErrorMessage(
                        technicalVisitQuestionsError,
                        t('technicalVisit.form.loadQuestionsError')
                    )}
                </Alert>
            )}

            {isTechnicalVisitError && technicalVisitId && (
                <Alert severity="error" sx={{ mt: 3 }}>
                    {getApiErrorMessage(
                        technicalVisitDetailError,
                        t('technicalVisit.form.loadVisitError')
                    )}
                </Alert>
            )}

            {formDefinition && !isFetchingQuestions && (
                <TechnicalVisitForm
                    formDefinition={formDefinition}
                    initialResponses={initialResponses}
                    onBack={() => navigate(-1)}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    readOnly={readOnly}
                />
            )}

            {!formDefinition && !isFetchingQuestions && !isQuestionsError && (
                <Alert severity="info" sx={{ mt: 3 }}>
                    {t('technicalVisit.form.empty')}
                </Alert>
            )}

            {!formDefinition && !isFetchingQuestions && (
                <Box sx={{ mt: 3 }}>
                    <Button variant="outlined" onClick={() => navigate(-1)}>
                        {t('common.goBack')}
                    </Button>
                </Box>
            )}

            <EstablishmentDrawer
                open={isEstablishmentDrawerOpen}
                setOpen={setIsEstablishmentDrawerOpen}
                title={t('technicalVisit.drawer.establishmentDetail')}
                establishment={establecimiento}
                loading={isFetchingEstablishment}
            />
        </>
    );
};

export default TechnicalVisit;
